// @flow
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import React, { useEffect } from 'react';
import { t } from 'javascripts/utils/withTranslation';
import DOMPurify from 'dompurify';
import RteToolbar from './RteToolbar';

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  // set all elements owning target to target=_blank
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const RteEditor = ({
  onChange,
  id,
  initialValue,
  inputId,
  isDisabled: initialIsDisabled,
  isError: initialIsError,
}: {
  onChange: (htmlContent: string) => void,
  id?: string,
  initialValue?: string,
  inputId?: string,
  isDisabled?: boolean,
  isError?: boolean,
}): React$MixedElement | null => {
  const [isDisabled, setIsDisabled] = React.useState(
    initialIsDisabled || false,
  );
  const editor = useEditor({
    editable: !isDisabled,
    extensions: [
      StarterKit.configure({
        link: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        enableClickSelection: true,
        defaultProtocol: 'https',
        protocols: ['http', 'https'],
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
        // isAllowedUri: (url, ctx) => {
        // customization of link validation can be done here
        // could add domain blacklist here
        // https://tiptap.dev/docs/editor/extensions/marks/link#isalloweduri
        // },
      }),
    ],
    editorProps: {
      attributes: {
        spellcheck: 'false',
        'aria-label': t('Texteingabe mit Formatierung'),
        'aria-multiline': 'true',
        'aria-readonly': 'false',
        role: 'textbox',
      },
    },
    shouldRerenderOnTransaction: true,
    immediatelyRender: true,
    onUpdate({ editor: updatedEditor }) {
      let html = updatedEditor.getHTML();
      if (html === '<p></p>') html = '';
      onChange(DOMPurify.sanitize(html));
      document
        .getElementById(inputId || 'null')
        ?.dispatchEvent(new Event('input', { bubbles: true }));
    },

    content: initialValue || '',
  });

  useEffect(() => {
    if (!inputId) {
      return undefined;
    }
    const $inputElement = document.getElementById(inputId);
    if (!$inputElement) {
      return undefined;
    }

    const $wrapper = $inputElement.nextElementSibling;

    const observer = new MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'disabled'
        ) {
          const isNowDisabled = $inputElement.hasAttribute('disabled');
          setIsDisabled(isNowDisabled);
          if (isNowDisabled) {
            $wrapper?.classList.add('input__rte-editor-wrapper--disabled');
          } else {
            $wrapper?.classList.remove('input__rte-editor-wrapper--disabled');
          }

          if (editor) {
            editor.setEditable(!isNowDisabled);
          }
        }

        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-invalid'
        ) {
          const isNowError = $inputElement.getAttribute('aria-invalid');
          if (isNowError && isNowError === 'true') {
            $wrapper?.classList.add('input__rte-editor-wrapper--error');
          } else {
            $wrapper?.classList.remove('input__rte-editor-wrapper--error');
          }
        }
      });
    });

    observer.observe($inputElement, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, [inputId]);

  return (
    <div
      className="input__rte-focus-container"
      onFocus={(e) => {
        e.target
          .closest('.input__rte-editor-wrapper')
          ?.classList.add('active-focus-overlay');
        e.currentTarget.classList.add('active-focus-overlay');
      }}
      onBlur={(e) => {
        e.target
          .closest('.input__rte-editor-wrapper')
          ?.classList.remove('active-focus-overlay');
        e.currentTarget.classList.remove('active-focus-overlay');
      }}
    >
      <RteToolbar editor={editor} isDisabled={isDisabled} />
      <EditorContent editor={editor} className="input__rte-editor" />
    </div>
  );
};

export default RteEditor;
